import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import PropTypes from "prop-types";

import { AlertIcon } from "../../__components__/icons";

import MessageChoice from "./MessageChoice";
import MessageStatus from "./MessageStatus";
import MessageSystem from "./MessageSystem";
import MessageText from "./MessageText";

import Markdown from "../../markdown/index";

function Message({
    _id,
    typing,
    type,
    content,
    author,
    first,
    last,
    status,
    replyTo,
    writtenBy,
}) {
    const timerRef = useRef(0);

    const [isHover, setIsHover] = useState(false);

    const isOperator = author?.type === "operator" || author?.type === "agent";

    const handleMouseEnter = () => {
        timerRef.current = setTimeout(() => {
            setIsHover(true);
        }, 600);
    };

    const handleMouseLeave = () => {
        clearTimeout(timerRef.current);
        setIsHover(false);
    };

    const handleClick = () => {
        const previousFocusedMessage = document.querySelector(
            ".ek-chat__message--animate-zoom"
        );

        previousFocusedMessage?.classList.remove(
            "ek-chat__message--animate-zoom"
        );

        const messageToFocus = document.getElementById(
            `message-${replyTo?._id}`
        );

        if (messageToFocus) {
            messageToFocus.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });

            setTimeout(() => {
                messageToFocus.classList.add("ek-chat__message--animate-zoom");
            }, 150);
        }
    };

    useEffect(() => () => clearTimeout(timerRef.current), []);

    if (type === "text")
        return (
            <div
                className={classNames({
                    "ek-chat__message--operator--wrapper": isOperator,
                    "ek-chat__message--visitor--wrapper":
                        author?.type === "visitor",
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {replyTo && replyTo?.content && !writtenBy.ai && (
                    <div
                        onClick={handleClick}
                        className="ek-chat__message--replyTo"
                    >
                        <Markdown content={replyTo?.content} replied />
                    </div>
                )}
                <div
                    className={classNames({
                        "ek-chat__message--operator--content": isOperator,
                        "ek-chat__message--visitor--content":
                            author?.type === "visitor",
                        "ek-chat__message--system--content": type === "system",
                    })}
                    id={`message-${_id}`}
                >
                    <MessageText
                        author={author}
                        content={content}
                        first={first}
                        last={last}
                        status={status}
                    />
                    {status.error && author.type === "visitor" && <AlertIcon />}
                </div>
                <MessageStatus
                    _id={_id}
                    type={type}
                    typing={typing}
                    author={author}
                    content={content}
                    status={status}
                    show={last || isHover}
                    onResetIsHover={handleMouseLeave}
                />
            </div>
        );

    if (type === "choice")
        return (
            <div
                className={classNames({
                    "ek-chat__message--operator--wrapper": isOperator,
                    "ek-chat__message--visitor--wrapper":
                        author?.type === "visitor",
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {replyTo && replyTo?.content && !writtenBy.ai && (
                    <div
                        onClick={handleClick}
                        className="ek-chat__message--replyTo"
                    >
                        <Markdown content={replyTo?.content} replied />
                    </div>
                )}
                <div
                    className={classNames({
                        "ek-chat__message--operator--content": isOperator,
                        "ek-chat__message--visitor--content":
                            author?.type === "visitor",
                    })}
                    id={`message-${_id}`}
                >
                    <MessageChoice
                        _id={_id}
                        author={author}
                        content={content}
                        first={first}
                        last={last}
                        status={status}
                    />
                    {status.error && author.type === "visitor" && <AlertIcon />}
                </div>
                <MessageStatus
                    _id={_id}
                    type={type}
                    typing={typing}
                    author={author}
                    content={content}
                    status={status}
                    show={last || isHover}
                    onResetIsHover={handleMouseLeave}
                />
            </div>
        );

    if (type === "system") return <MessageSystem content={content} />;

    return null;
}

export default Message;

Message.propTypes = {
    _id: PropTypes.string.isRequired,
    type: PropTypes.string,
    /**
     * Content to display
     */
    content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            text: PropTypes.string,
            interactions: PropTypes.arrayOf(
                PropTypes.shape({
                    type: PropTypes.string,
                    elements: PropTypes.arrayOf(
                        PropTypes.shape({
                            type: PropTypes.string,
                            label: PropTypes.string,
                            value: PropTypes.oneOfType([
                                PropTypes.string,
                                PropTypes.number,
                            ]),
                        })
                    ),
                })
            ),
        }),
    ]),
    /**
     * Author of the message
     */
    author: PropTypes.shape({
        _id: PropTypes.string,
        type: PropTypes.oneOf(["operator", "supervisor", "visitor", "agent"]),
        name: PropTypes.string,
    }),
    /**
     * Status of the message
     */
    status: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
    /**
     * Typing Mode
     */
    first: PropTypes.bool,
    last: PropTypes.bool,
    /**
     * Typing Mode
     */
    typing: PropTypes.bool,
    /**
     * Reply To
     */
    replyTo: PropTypes.shape({
        _id: PropTypes.string,
        type: PropTypes.string,
        content: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
        translation: PropTypes.shape({}),
        author: PropTypes.shape({
            _id: PropTypes.string,
            type: PropTypes.oneOf([
                "operator",
                "supervisor",
                "visitor",
                "agent",
            ]),
            name: PropTypes.string,
        }),
    }),
    writtenBy: PropTypes.shape({
        ai: PropTypes.bool,
        human: PropTypes.bool,
    }),
};

Message.defaultProps = {
    type: "",
    content: "",
    author: {},
    status: {},
    first: false,
    last: false,
    typing: false,
    replyTo: {},
    writtenBy: {},
};
